import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { Feedback, useResource } from "@/components/workflow/shared";
import {
  siteEquipmentApi,
  type EquipmentOverview,
  type EquipmentPlace,
} from "@/lib/api/oprema";
import { PlaceStatus, useEquipmentAccess } from "./shared";
import { EquipmentTasks } from "./EquipmentTasks";

export function EquipmentPlaceCards({
  data,
  places = data.mjesta,
  onChoose,
}: {
  data: EquipmentOverview;
  places?: EquipmentPlace[];
  onChoose?: (place: EquipmentPlace) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="equipment-place-list">
      {places.map((place) => {
        const balances = data.stanje.filter((s) => s.mjesto_id === place.id);
        const pieces = data.komadi.filter(
          (p) =>
            p.mjesto_id === place.id &&
            ["ispravno", "osteceno", "u_radioni"].includes(p.stanje),
        );
        const count = new Set([
          ...balances
            .filter((s) => s.ispravno + s.osteceno + s.u_radioni > 0)
            .map((s) => s.artikl_id),
          ...pieces.map((p) => p.artikl_id),
        ]).size;
        const arriving =
          balances.reduce((n, s) => n + s.u_prijevozu, 0) +
          data.komadi.filter(
            (p) => p.mjesto_id === place.id && p.stanje === "u_prijevozu",
          ).length;
        const damaged =
          balances.reduce((n, s) => n + s.osteceno, 0) +
          pieces.filter((p) => p.stanje === "osteceno").length;
        return (
          <Button
            key={place.id}
            variant="outline"
            className="equipment-placecard"
            aria-label={`Otvori ${place.naziv}`}
            onClick={() =>
              onChoose
                ? onChoose(place)
                : navigate(`/oprema/mjesta/${place.id}`)
            }
          >
            <span className="equipment-list-name">
              <b>{place.naziv}</b>
              <small>{count} vrsta opreme</small>
              <PlaceStatus place={place} />
            </span>
            <span className="equipment-list-badges">
              {arriving > 0 && <Badge tone="info">+{arriving} stiže</Badge>}
              {damaged > 0 && <Badge tone="bad">{damaged} oštećeno</Badge>}
              {!place.aktivno && <Badge tone="neutral">Neaktivno</Badge>}
            </span>
            <Icon name="chevronRight" />
          </Button>
        );
      })}
    </div>
  );
}

export function EquipmentHome({
  chooseSource = false,
  children,
}: {
  chooseSource?: boolean;
  children?: React.ReactNode;
}) {
  const access = useEquipmentAccess();
  const navigate = useNavigate();
  const resource = useResource(
    "equipment-home",
    useCallback((signal) => siteEquipmentApi.overview(signal), []),
  );
  const places =
    resource.data?.mjesta.filter(
      (place) => place.aktivno && (!chooseSource || access.canSend(place)),
    ) ?? [];
  const incoming = resource.data?.plocice.u_prijevozu ?? 0;
  const destinations = [
    ...new Set(
      resource.data?.stize?.map((transfer) => transfer.u_mjesto_id) ?? [],
    ),
  ];
  const transferNoun =
    incoming % 100 >= 11 && incoming % 100 <= 14
      ? "selidbi"
      : incoming % 10 === 1
        ? "selidba"
        : incoming % 10 >= 2 && incoming % 10 <= 4
          ? "selidbe"
          : "selidbi";
  return (
    <>
      {!chooseSource && <EquipmentTasks />}
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      {resource.loading && <p role="status">Učitavanje opreme…</p>}
      {!!resource.data?.plocice.u_prijevozu && !chooseSource && (
        <div className="equipment-todo equipment-warning">
          <div>
            <b>
              {incoming} {transferNoun} {incoming === 1 ? "čeka" : "čekaju"}{" "}
              potvrdu
            </b>
            <p>Provjeri opremu koja stiže.</p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                destinations.length === 1
                  ? `/oprema/mjesta/${destinations[0]}`
                  : "/oprema/mjesta",
              )
            }
          >
            Pogledaj
          </Button>
        </div>
      )}
      {children}
      <h2 className="equipment-label">
        {chooseSource ? "Odakle seliš opremu?" : "Moja gradilišta i mjesta"}
      </h2>
      {resource.data && (
        <EquipmentPlaceCards
          data={resource.data}
          places={places}
          onChoose={
            chooseSource
              ? (place) =>
                  navigate(`/oprema/mjesta/${place.id}?radnja=transfer`)
              : undefined
          }
        />
      )}
      {resource.data && !places.length && (
        <div className="equipment-card">
          <b>
            {chooseSource
              ? "Nema mjesta s kojeg smiješ slati opremu."
              : "Nema dostupnih mjesta."}
          </b>
          <p>
            {chooseSource
              ? "Slanje ovisi o tvojoj ulozi i dodijeljenim gradilištima. Obrati se skladištaru."
              : "Gradilišta dolaze iz Gradilišta. Bazu ili Radionu dodaje skladištar."}
          </p>
          {chooseSource && (
            <Button variant="outline" onClick={() => navigate("/oprema")}>
              Natrag na Opremu
            </Button>
          )}
        </div>
      )}
    </>
  );
}
